import React, { useState, useEffect } from "react";
import "./index.css";
import { NameCard } from "./NameCard";
import effortThemesService from "../../../apis/Business/ActionPlanThemeApi";
import unselectedCircle from "../../../assets/shapes_svg/ambition/actionplans/unselected-circle.svg";
import selectedCircle from "../../../assets/shapes_svg/ambition/actionplans/selected-circle.svg";
import unselectedSquare from "../../../assets/shapes_svg/ambition/actionplans/unselected-square.svg";
import selectedSquare from "../../../assets/shapes_svg/ambition/actionplans/selected-square.svg";
import { useUserAccess } from "../../../components/WriteAccessContext";

const Themes = ({ effortId }) => {
  const [selectedThemes, setSelectedThemes] = useState(new Set());
  const [editingTheme, setEditingTheme] = useState(null);
  const [newThemeValue, setNewThemeValue] = useState("");
  const [customThemes, setCustomThemes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userAccess } = useUserAccess();

  useEffect(() => {
    if (effortId) {
      fetchThemes();
    }
  }, [effortId]);

  const baseThemeGroups = [
    {
      id: "basis",
      items: [
        { id: "basis_impact", themes: "Basis Impact" },
        { id: "co2e", themes: "CO2e" },
        { id: "biodiversity", themes: "Biodiversity" },
      ],
    },
    {
      id: "environmental",
      items: [
        { id: "environment", themes: "Environment" },
        { id: "materials", themes: "Materials" },
        { id: "waste", themes: "Waste" },
      ],
    },
  ];

  const themeCategories = [
    {
      id: "social_gov",
      items: [
        { id: "social", themes: "Social" },
        { id: "water", themes: "Water" },
      ],
    },
    {
      id: "governance",
      items: [
        { id: "government", themes: "Government" },
        { id: "energy", themes: "Energy" },
      ],
    },
    {
      id: "business",
      items: [
        { id: "business_partner", themes: "Business Partner" },
        { id: "chemicals", themes: "Chemicals" },
      ],
    },
  ];

  const fetchThemes = async () => {
    try {
      const themes = await effortThemesService.getThemes(effortId);
      const selectedIds = new Set();
      const customThemesList = [];

      // Get all base themes for easier comparison
      const allBaseThemes = [...baseThemeGroups, ...themeCategories]
        .flatMap((g) => g.items)
        .map((item) => item.themes);

      themes.forEach((theme) => {
        // Always add the theme ID to selected themes
        selectedIds.add(theme.id);

        // If the theme name isn't in our base themes list, it's custom
        if (!allBaseThemes.includes(theme.themes)) {
          customThemesList.push({
            id: theme.id,
            themes: theme.themes,
            isCustom: true,
          });
        } else {
          // For base themes, we need to update the selectedIds with the matching base theme id
          const baseTheme = [...baseThemeGroups, ...themeCategories]
            .flatMap((g) => g.items)
            .find((item) => item.themes === theme.themes);

          if (baseTheme) {
            selectedIds.add(baseTheme.id);
          }
        }
      });

      setSelectedThemes(selectedIds);
      setCustomThemes(customThemesList);
    } catch (error) {
      console.error("Error fetching themes:", error);
    }
  };

  // Combine all themes into one array for rendering
  const allThemes = [
    ...baseThemeGroups.flatMap((group) => group.items),
    ...themeCategories.flatMap((group) => group.items),
    ...customThemes,
    { id: "add_theme", themes: "+", isAdd: true },
  ];

  const handleThemeToggle = async (themeId) => {
    if (isSubmitting || !effortId) return;
    setIsSubmitting(true);

    try {
      const isSelected = selectedThemes.has(themeId);
      const themeItem = allThemes.find((t) => t.id === themeId);
      if (!themeItem) return;

      if (!isSelected) {
        const response = await effortThemesService.createTheme(effortId, {
          themes: themeItem.themes,
        });
        setSelectedThemes((prev) => new Set([...prev, response.id, themeId]));
      } else {
        await effortThemesService.deleteTheme(effortId, themeId);
        setSelectedThemes((prev) => {
          const newSelected = new Set(prev);
          newSelected.delete(themeId);
          return newSelected;
        });
      }
    } catch (error) {
      console.error("Error toggling theme:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddThemeClick = () => {
    setEditingTheme("add_theme");
    setNewThemeValue("");
  };

  const handleNewThemeSubmit = async () => {
    if (newThemeValue.trim() && effortId) {
      try {
        const response = await effortThemesService.createTheme(effortId, {
          themes: newThemeValue.trim(),
        });

        const newTheme = {
          id: response.id,
          themes: newThemeValue.trim(),
          isCustom: true,
        };

        setCustomThemes((prev) => [...prev, newTheme]);
        setSelectedThemes((prev) => new Set([...prev, response.id]));
      } catch (error) {
        console.error("Error creating custom theme:", error);
      }
    }
    setEditingTheme(null);
    setNewThemeValue("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleNewThemeSubmit();
    }
  };

  const CustomCheckbox = ({ checked, onChange, isFirstColumn, disabled }) => (
    <img
      src={
        isFirstColumn
          ? checked
            ? selectedSquare
            : unselectedSquare
          : checked
          ? selectedCircle
          : unselectedCircle
      }
      alt={checked ? "Selected" : "Unselected"}
      onClick={() => !disabled && onChange()}
      style={{
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        width: "20px",
        height: "20px",
      }}
    />
  );

  return (
    <div style={{ padding: "20px 20px 20px 0", display: "flex", gap: "20px" }}>
      <NameCard
        deptName={"Themes"}
        isBottom={false}
        className={"other-effort-container"}
      />
      <div
        style={{ display: "flex", flexDirection: "column" }}
        className="action-plans-parent-bubble"
      >
        <div
          style={{
            fontSize: "14px",
            color: "#1B3764",
            marginBottom: "15px",
            textAlign: "center",
          }}
        >
          Efforts are pulled to report based on Themes
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "10px",
          }}
        >
          {allThemes.map((theme, index) => (
            <div
              key={theme.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px",
                fontSize: "14px",
                color: "#1B3764",
              }}
            >
              {theme.isAdd ? (
                editingTheme === theme.id && userAccess === "write" ? (
                  <input
                    type="text"
                    value={newThemeValue}
                    onChange={(e) => setNewThemeValue(e.target.value)}
                    onBlur={handleNewThemeSubmit}
                    onKeyPress={handleKeyPress}
                    autoFocus
                    style={{
                      width: "100%",
                      padding: "4px 8px",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                ) : (
                  <button
                    onClick={handleAddThemeClick}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      color: "#1B3764",
                      padding: "0 8px",
                    }}
                    disabled={userAccess !== "write"}
                  >
                    {theme.themes}
                  </button>
                )
              ) : (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  {userAccess === "write" && (
                    <CustomCheckbox
                      checked={selectedThemes.has(theme.id)}
                      onChange={() => handleThemeToggle(theme.id)}
                      isFirstColumn={index % 3 === 0}
                      disabled={isSubmitting}
                    />
                  )}
                  {theme.themes}
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Themes;
